import DOMPurify from "dompurify";
import { DELIVERED_SERVICE_STATUS_OPTIONS } from "Constants/constant";
import { formatDateIntoDMYToDisplay, gethoursAndMins, timeToQuantity } from "Utils/utils";

export const isCosCaseNoteLocked = (note) =>
  note?.cos_ndis_claim_id != null || note?.cos_invoice_id != null;

export const getCosCaseNoteStatusLabel = (note) => {
  const value = note?.status ?? note?.invoice_status;
  if (value == null) return "—";
  const match = DELIVERED_SERVICE_STATUS_OPTIONS.find((opt) => opt.value === value);
  return match?.label ?? value;
};

export const isCosCaseNoteBillable = (note) =>
  note?.bill_types === "billable_hours" || note?.bill_types === "true";

export const formatCosCaseNoteDuration = (note) =>
  timeToQuantity(gethoursAndMins(note?.hours, note?.mints));

export const formatCosCaseNoteDeliveredDate = (note) =>
  formatDateIntoDMYToDisplay(note?.delivered_date) || "—";

export const formatCosCaseNoteCreatedDate = (note) =>
  formatDateIntoDMYToDisplay(note?.created_at) || "—";

/** Treat null / empty / literal "null" as no note content (optional Case Notes field). */
export const hasCosCaseNoteBody = (html) => {
  if (html == null) return false;
  const raw = String(html).trim();
  if (raw === "" || raw.toLowerCase() === "null") return false;
  return stripHtmlToPlainText(html).length > 0;
};

/** List API shape: notes_preview + has_more_notes (full notes loaded on demand). */
export const hasCosCaseNoteListBody = (note) => {
  if (!note) return false;
  if (note.has_more_notes) return true;
  const preview = note.notes_preview;
  if (preview == null) return hasCosCaseNoteBody(note.notes);
  const raw = String(preview).trim();
  return raw !== "" && raw.toLowerCase() !== "null";
};

export const getCosCaseNoteListPreviewText = (note) => {
  if (!note) return "";
  if (note.notes_preview != null) {
    const raw = String(note.notes_preview).trim();
    return raw.toLowerCase() === "null" ? "" : raw;
  }
  return stripHtmlToPlainText(note.notes);
};

export const shouldShowCosCaseNoteMore = (note) => {
  if (!note) return false;
  if (typeof note.has_more_notes === "boolean") return note.has_more_notes;
  return shouldTruncateNote(note.notes);
};

export const sanitizeNoteHtml = (html) => {
  if (html == null) return "";
  const raw = String(html).trim();
  if (raw === "" || raw.toLowerCase() === "null") return "";
  return DOMPurify.sanitize(raw, { USE_PROFILES: { html: true } });
};

export const stripHtmlToPlainText = (html) => {
  if (html == null) return "";
  const raw = String(html).trim();
  if (raw === "" || raw.toLowerCase() === "null") return "";
  const clean = sanitizeNoteHtml(html);
  if (!clean) return "";
  if (typeof document === "undefined") return clean.replace(/<[^>]*>/g, "").trim();
  const el = document.createElement("div");
  el.innerHTML = clean;
  const text = (el.textContent || el.innerText || "").trim();
  if (text.toLowerCase() === "null") return "";
  return text;
};

export const shouldTruncateNote = (html, maxLen = 280) =>
  stripHtmlToPlainText(html).length > maxLen;

/** Normalize list + legacy single-attachment shapes for the card UI. */
export const normalizeCosCaseNoteAttachments = (note) => {
  const fromRelation = Array.isArray(note?.attachments) ? note.attachments : [];
  const items = fromRelation.filter(
    (item) => item?.attachments || item?.attachment_url
  );
  if (items.length > 0) return items;

  if (note?.attachment) {
    return [
      {
        id: null,
        original_name: note.file_name,
        attachments: note.attachment,
        attachment_url: note.attachment_url || null,
      },
    ];
  }

  return [];
};
